import { put, call, fork, race, take, takeEvery } from 'redux-saga/effects'
import { ulid } from 'ulid'

import {
  getS3SignedUrl as getS3SignedUrlApi,
  createFileMetadata as createFileMetadataApi,
} from 'Service/Api'

import { getUser } from 'Service/localUser'

import { getUserProfile } from 'Redux/Actions/UserActions'
import * as UploadFileTypes from 'Redux/Types/UploadFileTypes'
import * as UploadFileActions from 'Redux/Actions/UploadFileActions'
import * as LedgerActions from 'Redux/Actions/LedgerActions'
import * as LedgerTypes from 'Redux/Types/LedgerTypes'
import { createUploadFileChannel } from './common'

function* createFileMetadata(fileObject) {
  const { id, file, meta } = fileObject
  try {
    const { data } = yield call(createFileMetadataApi, [{ ...meta, filename: file.name }])
    const { failures, successes } = data
    yield put(
      UploadFileActions.createFileMetadataSucceeded({ id, file, meta, failures, successes }),
    )

    const { hasReceipts } = getUser()
    if (hasReceipts === false) {
      yield put(getUserProfile({ remote: true }))
    }
  } catch (error) {
    yield put(UploadFileActions.createFileMetadataFailed({ id, file, meta }))
  }
}

function* fetchPresignedS3URLS(files) {
  yield put(UploadFileActions.fetchS3PresignedURLs())
  const { data } = yield call(
    getS3SignedUrlApi,
    files.map(({ name, type: contentType }) => {
      if (!contentType && name.includes('.rar')) {
        return { contentType: 'application/vnd.rar' }
      } else if (!contentType && name.includes('.7z')) {
        return { contentType: 'application/x-7z-compressed' }
      }
      return { contentType }
    }),
  )
  yield put(UploadFileActions.fetchS3PresignedURLsSucceeded())
  return data
}

function* uploadFile({ fileObject, onError, onProgress, onSuccess }) {
  const channel = yield call(createUploadFileChannel, fileObject.meta.url, fileObject.file)

  while (true) {
    const { progress = 0, error, success } = yield take(channel)
    if (success) {
      yield call(onSuccess, fileObject)
      return
    }
    if (error) {
      yield call(onError, fileObject)
      return
    }
    yield call(onProgress, fileObject, progress)
  }
}

function* uploadFiles({ payload }) {
  try {
    const s3URLObjects = yield call(fetchPresignedS3URLS, payload)
    const mappedURLObjects = s3URLObjects.map((meta, index) => ({ file: payload[index], meta }))

    function* onError(fileObject, error) {
      yield put(UploadFileActions.uploadFilesFailed({ ...fileObject, error }))
    }
    function* onProgress(fileObject, progress) {
      yield put(UploadFileActions.uploadFilesProgress({ ...fileObject, progress }))
    }
    function* onSuccess(fileObject) {
      yield put(UploadFileActions.uploadFilesSucceeded(fileObject))
      yield call(createFileMetadata, fileObject)
    }

    for (let i = mappedURLObjects.length - 1; i >= 0; --i) {
      const fileObject = { ...mappedURLObjects[i], id: ulid() }
      yield fork(uploadFile, { fileObject, onError, onProgress, onSuccess })
    }
  } catch (error) {
    yield put(UploadFileActions.uploadFilesFailed())
  }
}

function* uploadAttachments({ payload }) {
  try {
    const s3URLObjects = yield call(fetchPresignedS3URLS, payload.attachments)
    const mappedURLObjects = s3URLObjects.map((meta, index) => ({
      file: payload.attachments[index],
      meta,
    }))

    function* onError(fileObject, error) {
      yield put(UploadFileActions.uploadFilesFailed({ ...fileObject, error }))
    }
    function* onProgress(fileObject, progress) {
      yield put(UploadFileActions.uploadFilesProgress({ ...fileObject, progress }))
    }
    function* onSuccess(fileObject) {
      yield put(UploadFileActions.uploadFilesSucceeded(fileObject))
      yield put(UploadFileActions.uploadAttachmentsSucceeded(fileObject))
      yield put(
        LedgerActions.attachDocuments({ attachments: mappedURLObjects, type: payload.type }),
      )

      const [error] = yield race([
        take(LedgerTypes.ATTACH_DOCUMENTS_FAILED),
        take(LedgerTypes.ATTACH_DOCUMENTS_SUCCEEDED),
      ])

      if (error) {
        yield put(UploadFileActions.createFileMetadataFailed(fileObject))
      } else {
        yield put(UploadFileActions.createFileMetadataSucceeded(fileObject))
      }
    }

    for (let i = mappedURLObjects.length - 1; i >= 0; --i) {
      const fileObject = { ...mappedURLObjects[i], id: ulid() }
      yield fork(uploadFile, { fileObject, onError, onProgress, onSuccess })
    }
  } catch (error) {
    yield put(UploadFileActions.uploadFilesFailed())
  }
}

export default function* UploadFileSaga() {
  yield takeEvery(UploadFileTypes.UPLOAD_FILES, uploadFiles)
  yield takeEvery(UploadFileTypes.UPLOAD_ATTACHMENTS, uploadAttachments)
}
